/**
 * `/t/$tenantId/catalogue`: the "Perfis de coleta" screen -- one card per collection profile the
 * organisation holds, the seeded initial set included. Each profile is the whole collection
 * configuration; its schemas and query keys are exported for the profile editor and the
 * add-account dialog.
 *
 * The schemas are transcribed field for field against `api`'s
 * `routers/catalogues.py`, `routers/monitoring_profiles.py` and `schemas/tracking.py`.
 * `active` and the `night_reduction_*` fields default rather than require: other screens mock
 * this response without them.
 *
 * "Duplicar" and "Novo perfil" share `NewProfileDialog`: a copy re-posts the source profile's
 * own fields under the new name, a blank one posts to `monitoring-profiles/blank`.
 */

import { Plus } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Page, PageHeader } from '../../../chrome/page';
import { ErrorState } from '../../../chrome/state';
import { defineCatalogue } from '../../../i18n';
import type { ApiFailureError } from '../../../lib/http/errors';
import { Button } from '../../../ui/button';
import { useApiClient } from '../../session/client';
import { GET_ROUTES, routePath } from '../../../lib/http/routes';
import type { MonitoringProfileListed, MonitoringProfilePriced } from '../api/profileSchemas';
import { useCan } from '../../session/orgContext';
import { NewProfileDialog } from './catalogue/NewProfileDialog';
import { ProfileCards } from './catalogue/ProfileCards';

export const catalogueCatalogue = defineCatalogue(
  {
    title: 'Perfis de coleta',
    'profiles.label': 'Perfis de coleta da organização',

    'action.edit': 'Editar',
    'action.editLabel': 'Editar {name}',
    'action.duplicate': 'Duplicar',
    'action.delete': 'Excluir',
    'action.pause': 'Pausar coleta',
    'action.resume': 'Retomar coleta',
    'actions.label': 'Mais ações de {name}',

    'status.paused': 'Pausado',

    'usage.title': 'Uso atual',
    'usage.accounts': 'Contas usando',
    'usage.credits': 'Créditos por dia (estimado)',
    unpriced: 'sem preço',
    'usage.examplesMore.one': 'e mais {count} conta',
    'usage.examplesMore.other': 'e mais {count} contas',
    'usage.none': 'Nenhuma conta usa este perfil.',

    'kind.off': 'Desligado',
    'kind.auto': 'Automático',
    'kind.on': 'Ligado',
    'kind.timeline': 'Coleta de {kind}',
    'flag.on': '{flag}: ligado',
    'flag.off': '{flag}: desligado',
    'flag.comments_enabled': 'Comentários',
    'flag.likers_enabled': 'Curtidas',

    'pause.title': 'Pausar a coleta de {name}?',
    'pause.body': 'Nenhuma conta deste perfil é coletada até você retomar.',
    'resume.title': 'Retomar a coleta de {name}?',
    'resume.body': 'As contas deste perfil voltam a ser coletadas na próxima rodada.',
    'impact.accounts': 'Contas afetadas',
    'impact.saves': 'Créditos poupados por dia',
    'impact.costs': 'Créditos a mais por dia',
    'toggle.failed': 'Não foi possível mudar a coleta deste perfil. Tente de novo.',

    'delete.title': 'Excluir {name}?',
    'delete.body': 'Esta ação não pode ser desfeita.',
    'delete.inUse': 'As contas que usam este perfil passam a usar outro daqui em diante.',
    'delete.inUseElsewhere':
      'Este perfil ainda é o padrão de uma campanha. Escolha o perfil que fica no lugar dele.',
    'delete.target': 'Mover para o perfil',
    'delete.targetPlaceholder': 'Escolha um perfil',
    'delete.failed': 'Não foi possível excluir este perfil. Tente de novo.',
    'collectionChange.resume.one': 'A coleta será retomada para {count} conta',
    'collectionChange.resume.other': 'A coleta será retomada para {count} contas',
    'collectionChange.pause.one': 'A coleta será pausada para {count} conta',
    'collectionChange.pause.other': 'A coleta será pausada para {count} contas',

    'profiles.empty': 'Nenhum perfil de coleta ainda.',

    'newProfile.trigger': 'Novo perfil',
    'newProfile.title': 'Novo perfil de coleta',
    'newProfile.duplicateTitle': 'Duplicar perfil',
    'newProfile.copyName': '{name} (cópia)',
    'newProfile.name': 'Nome',
    'newProfile.source': 'Começar a partir de',
    'newProfile.sourceBlank': 'Do zero, sem copiar nada',
    'newProfile.sourcePrefix': 'Copiar de: {name}',
    'newProfile.submit': 'Criar e configurar',
    'newProfile.nameTaken': 'Já existe um perfil com este nome. Escolha outro.',
  },
  {
    title: 'Collection profiles',
    'profiles.label': "The organisation's collection profiles",

    'action.edit': 'Edit',
    'action.editLabel': 'Edit {name}',
    'action.duplicate': 'Duplicate',
    'action.delete': 'Delete',
    'action.pause': 'Pause collection',
    'action.resume': 'Resume collection',
    'actions.label': 'More actions for {name}',

    'status.paused': 'Paused',

    'usage.title': 'Current usage',
    'usage.accounts': 'Accounts using it',
    'usage.credits': 'Credits per day (estimated)',
    unpriced: 'no price',
    'usage.examplesMore.one': 'and {count} more account',
    'usage.examplesMore.other': 'and {count} more accounts',
    'usage.none': 'No account uses this profile.',

    'kind.off': 'Off',
    'kind.auto': 'Automatic',
    'kind.on': 'On',
    'kind.timeline': '{kind} collection',
    'flag.on': '{flag}: on',
    'flag.off': '{flag}: off',
    'flag.comments_enabled': 'Comments',
    'flag.likers_enabled': 'Likes',

    'pause.title': 'Pause collection for {name}?',
    'pause.body': 'No account on this profile is collected until you resume.',
    'resume.title': 'Resume collection for {name}?',
    'resume.body': 'The accounts on this profile are collected again from the next round.',
    'impact.accounts': 'Accounts affected',
    'impact.saves': 'Credits saved per day',
    'impact.costs': 'Extra credits per day',
    'toggle.failed': "Could not change this profile's collection. Try again.",

    'delete.title': 'Delete {name}?',
    'delete.body': 'This cannot be undone.',
    'delete.inUse': 'The accounts on this profile use another one from now on.',
    'delete.inUseElsewhere':
      "This profile is still a campaign's default. Pick the profile that takes its place.",
    'delete.target': 'Move to profile',
    'delete.targetPlaceholder': 'Pick a profile',
    'delete.failed': 'Could not delete this profile. Try again.',
    'collectionChange.resume.one': 'Collection will resume for {count} account',
    'collectionChange.resume.other': 'Collection will resume for {count} accounts',
    'collectionChange.pause.one': 'Collection will be paused for {count} account',
    'collectionChange.pause.other': 'Collection will be paused for {count} accounts',

    'profiles.empty': 'No collection profiles yet.',

    'newProfile.trigger': 'New profile',
    'newProfile.title': 'New collection profile',
    'newProfile.duplicateTitle': 'Duplicate profile',
    'newProfile.copyName': '{name} (copy)',
    'newProfile.name': 'Name',
    'newProfile.source': 'Start from',
    'newProfile.sourceBlank': 'From scratch, copying nothing',
    'newProfile.sourcePrefix': 'Copy from: {name}',
    'newProfile.submit': 'Create and configure',
    'newProfile.nameTaken': 'A profile with this name already exists. Pick another.',
  },
);

export const ct = catalogueCatalogue.t;
export const ctPlural = catalogueCatalogue.plural;

export {
  mediaKindSchema,
  mediaEnablementSchema,
  priceSchema,
  profileMediaSchema,
  profileAudioSchema,
  monitoringProfileOutSchema,
  monitoringProfileSchema,
  profileUsageExampleSchema,
  monitoringProfileListedSchema,
  type Price,
  type MonitoringProfileOut,
  type MonitoringProfilePriced,
  type ProfileUsageExample,
  type MonitoringProfileListed,
} from '../api/profileSchemas';

export const PROFILES_PATH = (tenantId: string) => `/tenants/${tenantId}/monitoring-profiles`;
/** `routers/monitoring_profiles.py`'s `create_blank_monitoring_profile`:
 *  a deliberately separate router from the one `PROFILES_PATH` above posts
 *  to, reused here only for its path. */
export const PROFILES_BLANK_PATH = (tenantId: string) => `${PROFILES_PATH(tenantId)}/blank`;

/** Exported for the stories beside each list, which seed a `QueryClient` on
 *  these same keys rather than restating them. */
export const PROFILES_QUERY_KEY = (tenantId: string) =>
  ['control', 'catalogue', 'monitoring-profiles', tenantId] as const;

export function useMonitoringProfiles(tenantId: string) {
  const client = useApiClient();
  return useQuery<{ items: MonitoringProfileListed[] }, ApiFailureError>({
    queryKey: PROFILES_QUERY_KEY(tenantId),
    queryFn: () =>
      client.getParsed(
        routePath(GET_ROUTES.monitoringProfilesCatalogue, { tenantId }),
        GET_ROUTES.monitoringProfilesCatalogue.schema,
      ),
    enabled: tenantId !== '',
  });
}

/** A slug is unique per tenant (`uq_*_tenant_slug`). */
export function copySlug(slug: string): string {
  return `${slug}-${crypto.randomUUID().slice(0, 8)}`;
}

export function duplicateProfileBody(profile: MonitoringProfilePriced): unknown {
  return {
    slug: copySlug(profile.slug),
    name: profile.name,
    description: profile.description,
    night_reduction_enabled: profile.night_reduction_enabled,
    night_reduction_max_interval_seconds: profile.night_reduction_max_interval_seconds,
    night_reduction_start_local: profile.night_reduction_start_local,
    night_reduction_stop_local: profile.night_reduction_stop_local,
    media: profile.media.map((item) => ({
      media_kind: item.media_kind,
      enabled: item.enabled,
      periods: item.periods,
      story_interval_seconds: item.story_interval_seconds,
      comments_enabled: item.comments_enabled,
      likers_enabled: item.likers_enabled,
      comment_cap_per_post: item.comment_cap_per_post,
    })),
    audio: profile.audio,
  };
}

export function Catalogue(props: { tenantId: string }): ReactNode {
  const { tenantId } = props;
  const canWrite = useCan('control_plane.write');
  const profiles = useMonitoringProfiles(tenantId);
  const [duplicating, setDuplicating] = useState<MonitoringProfileListed | null>(null);
  const items = profiles.data?.items ?? [];

  return (
    <Page>
      <PageHeader
        title={ct('title')}
        actions={
          canWrite ? (
            <NewProfileDialog
              tenantId={tenantId}
              profiles={items}
              trigger={
                <Button type="button">
                  <Plus aria-hidden />
                  {ct('newProfile.trigger')}
                </Button>
              }
            />
          ) : undefined
        }
      />
      {profiles.isError ? (
        <ErrorState failure={profiles.error.failure} onRetry={() => void profiles.refetch()} />
      ) : (
        <ProfileCards
          tenantId={tenantId}
          profiles={items}
          loading={profiles.isPending}
          canWrite={canWrite}
          onDuplicate={setDuplicating}
        />
      )}
      {duplicating !== null && (
        <NewProfileDialog
          key={duplicating.id}
          tenantId={tenantId}
          profiles={items}
          open
          onOpenChange={(open) => {
            if (!open) setDuplicating(null);
          }}
          title={ct('newProfile.duplicateTitle')}
          initialSourceId={duplicating.id}
          initialName={ct('newProfile.copyName', { name: duplicating.name })}
        />
      )}
    </Page>
  );
}
